import type {
    OffsetPageDTO,
    OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type {
    ScheduleChangeLogDetailDTO,
    ScheduleChangeLogListItemDTO,
    ScheduleChangeLogListQueryDTO,
} from "../../domain/contracts/schedule-logs";
import type {
    AlternativeRoomDTO,
    BulkCreateClassSessionsDTO,
    BulkUpdateClassSessionInstructorsDTO,
    BulkUpdateClassSessionInstructorsPreviewDTO,
    BulkUpdateClassSessionInstructorsPreviewResultDTO,
    BulkUpdateClassSessionTemplateDTO,
    BulkUpdateClassSessionTemplatePreviewDTO,
    BulkUpdateClassSessionTemplatePreviewResultDTO,
    ClassSessionDTO,
    CreateClassSessionDTO,
    FindAlternativeRoomsDTO,
    RoomConflictCheckDTO,
    RoomConflictResultDTO,
    ScheduleChangeLogDTO,
    UpdateClassSessionDTO,
} from "../../domain/contracts/sessions";
import type {
    ClassTemplateDTO,
    CreateClassTemplateDTO,
    UpdateClassTemplateDTO,
} from "../../domain/contracts/templates";
import type { EligibleInstructorDTO } from "../../domain/repo/templates/get-eligible-instructors";
import type { ClassTemplateUsageStats } from "../../domain/repo/templates/get-usage-stats";

export const classesApi = {
  templates: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<ClassTemplateDTO>> {
      const url = new URL(`${config.apiUrl}/classes/templates`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<ClassTemplateDTO>>(url.toString());
    },
    async get(id: string): Promise<ClassTemplateDTO> {
      return fetcher.get<ClassTemplateDTO>(
        `${config.apiUrl}/classes/templates/${id}`,
      );
    },
    async getUsageStats(id: string): Promise<ClassTemplateUsageStats> {
      return fetcher.get<ClassTemplateUsageStats>(
        `${config.apiUrl}/classes/templates/${id}/usage-stats`,
      );
    },
    async getEligibleInstructors(id: string): Promise<EligibleInstructorDTO[]> {
      return fetcher.get<EligibleInstructorDTO[]>(
        `${config.apiUrl}/classes/templates/${id}/eligible-instructors`,
      );
    },
    async create(input: CreateClassTemplateDTO): Promise<ClassTemplateDTO> {
      return fetcher.post<ClassTemplateDTO>(
        `${config.apiUrl}/classes/templates`,
        input,
      );
    },
    async update(
      id: string,
      input: UpdateClassTemplateDTO,
    ): Promise<ClassTemplateDTO> {
      return fetcher.put<ClassTemplateDTO>(
        `${config.apiUrl}/classes/templates/${id}`,
        input,
      );
    },
    async toggleStatus(
      id: string,
      status: "active" | "inactive",
    ): Promise<ClassTemplateDTO> {
      return fetcher.put<ClassTemplateDTO>(
        `${config.apiUrl}/classes/templates/${id}/toggle-status`,
        { status },
      );
    },
    async remove(id: string): Promise<{ success: boolean }> {
      return fetcher.delete<{ success: boolean }>(
        `${config.apiUrl}/classes/templates/${id}`,
      );
    },
    async bulkDelete(ids: string[]): Promise<{ deletedCount: number }> {
      return fetcher.post<{ deletedCount: number }>(
        `${config.apiUrl}/classes/templates/bulk-delete`,
        { ids },
      );
    },
    async bulkToggleStatus(
      ids: string[],
      status: "active" | "inactive",
    ): Promise<{ updatedCount: number }> {
      return fetcher.post<{ updatedCount: number }>(
        `${config.apiUrl}/classes/templates/bulk-toggle-status`,
        { ids, status },
      );
    },
  },
  sessions: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<ClassSessionDTO>> {
      const url = new URL(`${config.apiUrl}/classes/sessions`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<ClassSessionDTO>>(url.toString());
    },
    async get(id: string): Promise<ClassSessionDTO> {
      return fetcher.get<ClassSessionDTO>(
        `${config.apiUrl}/classes/sessions/${id}`,
      );
    },
    async getAttendees(id: string): Promise<{
      attendees: Array<{
        id: string;
        studentId: string;
        studentName: string;
        studentMemberId: string;
        checkedInAt: string;
      }>;
    }> {
      return fetcher.get<{
        attendees: Array<{
          id: string;
          studentId: string;
          studentName: string;
          studentMemberId: string;
          checkedInAt: string;
        }>;
      }>(`${config.apiUrl}/classes/sessions/${id}/attendees`);
    },
    async getInstructorAttendees(id: string): Promise<{
      attendees: Array<{
        id: string;
        instructorId: string;
        instructorName: string;
        checkedInAt: string;
        isSubstitute: boolean | null;
        instructorRole: "lead" | "assist" | null;
      }>;
    }> {
      return fetcher.get<{
        attendees: Array<{
          id: string;
          instructorId: string;
          instructorName: string;
          checkedInAt: string;
          isSubstitute: boolean | null;
          instructorRole: "lead" | "assist" | null;
        }>;
      }>(`${config.apiUrl}/classes/sessions/${id}/instructor-attendees`);
    },
    async getEligibleStudents(
      id: string,
      params?: { q?: string; limit?: number },
    ): Promise<{
      students: Array<{ id: string; name: string; memberId: string }>;
      studentCheckInEnabled: boolean;
      engagementKind: "standard" | "partner_instructor_only";
    }> {
      const url = new URL(
        `${config.apiUrl}/classes/sessions/${id}/eligible-students`,
      );
      if (params?.q != null) url.searchParams.set("q", params.q);
      if (params?.limit != null)
        url.searchParams.set("limit", String(params.limit));
      return fetcher.get<{
        students: Array<{ id: string; name: string; memberId: string }>;
        studentCheckInEnabled: boolean;
        engagementKind: "standard" | "partner_instructor_only";
      }>(url.toString());
    },
    async getAbsences(id: string): Promise<{
      absences: Array<{
        id: string;
        studentId: string;
        studentName: string;
        memberId: string;
        recordedBy: string;
        recordedByName: string;
        recordedAt: string;
      }>;
    }> {
      return fetcher.get<{
        absences: Array<{
          id: string;
          studentId: string;
          studentName: string;
          memberId: string;
          recordedBy: string;
          recordedByName: string;
          recordedAt: string;
        }>;
      }>(`${config.apiUrl}/classes/sessions/${id}/absences`);
    },
    /** นับจำนวนนักเรียน eligible ต่อ ຊົ່ວໂມງຮຽນ (active package + rule ตรง) สำหรับ Check-in Hub */
    async getEligibleCounts(
      sessionIds: string[],
    ): Promise<{ counts: Record<string, number> }> {
      if (sessionIds.length === 0) return { counts: {} };
      return fetcher.post<{ counts: Record<string, number> }>(
        `${config.apiUrl}/classes/sessions/eligible-counts`,
        { sessionIds },
      );
    },
    async getCalendar(
      start: string,
      end: string,
      filters?: {
        roomId?: string;
        instructorId?: string;
        classTypeId?: string;
        status?: string;
        templateId?: string;
        locationKind?: "studio" | "off_site";
        engagementKind?: "standard" | "partner_instructor_only";
      },
    ): Promise<{ data: ClassSessionDTO[] }> {
      const url = new URL(`${config.apiUrl}/classes/sessions/calendar`);
      url.searchParams.set("start", start);
      url.searchParams.set("end", end);
      if (filters?.roomId) url.searchParams.set("roomId", filters.roomId);
      if (filters?.instructorId)
        url.searchParams.set("instructorId", filters.instructorId);
      if (filters?.classTypeId)
        url.searchParams.set("classTypeId", filters.classTypeId);
      if (filters?.status) url.searchParams.set("status", filters.status);
      if (filters?.templateId)
        url.searchParams.set("templateId", filters.templateId);
      if (filters?.locationKind)
        url.searchParams.set("locationKind", filters.locationKind);
      if (filters?.engagementKind)
        url.searchParams.set("engagementKind", filters.engagementKind);
      return fetcher.get<{ data: ClassSessionDTO[] }>(url.toString());
    },
    getCalendarExportUrl(
      start: string,
      end: string,
      filters?: {
        roomId?: string;
        instructorId?: string;
        classTypeId?: string;
        status?: string;
        templateId?: string;
        locationKind?: "studio" | "off_site";
        engagementKind?: "standard" | "partner_instructor_only";
      },
    ): string {
      const url = new URL(`${config.apiUrl}/classes/sessions/calendar/export`);
      url.searchParams.set("start", start);
      url.searchParams.set("end", end);
      url.searchParams.set("format", "csv");
      if (filters?.roomId) url.searchParams.set("roomId", filters.roomId);
      if (filters?.instructorId)
        url.searchParams.set("instructorId", filters.instructorId);
      if (filters?.classTypeId)
        url.searchParams.set("classTypeId", filters.classTypeId);
      if (filters?.status) url.searchParams.set("status", filters.status);
      if (filters?.templateId)
        url.searchParams.set("templateId", filters.templateId);
      if (filters?.locationKind)
        url.searchParams.set("locationKind", filters.locationKind);
      if (filters?.engagementKind)
        url.searchParams.set("engagementKind", filters.engagementKind);
      return url.toString();
    },
    async checkConflict(
      input: RoomConflictCheckDTO,
    ): Promise<RoomConflictResultDTO> {
      return fetcher.post<RoomConflictResultDTO>(
        `${config.apiUrl}/classes/sessions/check-conflict`,
        input,
      );
    },
    async getCheckConflicts(
      start: string,
      end: string,
      roomId?: string,
    ): Promise<{
      conflicts: Array<{ id: string; type: "class" | "private" }>;
    }> {
      const url = new URL(`${config.apiUrl}/classes/sessions/check-conflicts`);
      url.searchParams.set("start", start);
      url.searchParams.set("end", end);
      if (roomId) url.searchParams.set("roomId", roomId);
      return fetcher.get<{
        conflicts: Array<{ id: string; type: "class" | "private" }>;
      }>(url.toString());
    },
    async findAlternativeRooms(
      input: FindAlternativeRoomsDTO,
    ): Promise<{ rooms: AlternativeRoomDTO[] }> {
      return fetcher.post<{ rooms: AlternativeRoomDTO[] }>(
        `${config.apiUrl}/classes/sessions/find-alternative-rooms`,
        input,
      );
    },
    async bulkCreate(
      input: BulkCreateClassSessionsDTO,
    ): Promise<{
      createdCount: number;
      sessions: ClassSessionDTO[];
      conflictingDates: Array<{
        date: string;
        startTime: string;
        endTime: string;
        roomId: string;
        roomName: string;
        message: string;
      }>;
    }> {
      return fetcher.post<{
        createdCount: number;
        sessions: ClassSessionDTO[];
        conflictingDates: Array<{
          date: string;
          startTime: string;
          endTime: string;
          roomId: string;
          roomName: string;
          message: string;
        }>;
      }>(`${config.apiUrl}/classes/sessions/bulk-create`, input);
    },
    async previewBulkUpdateTemplate(
      input: BulkUpdateClassSessionTemplatePreviewDTO,
    ): Promise<BulkUpdateClassSessionTemplatePreviewResultDTO> {
      return fetcher.post(
        `${config.apiUrl}/classes/sessions/bulk-update-template/preview`,
        input,
      );
    },
    async bulkUpdateTemplate(
      input: BulkUpdateClassSessionTemplateDTO,
    ): Promise<{ updatedCount: number; skippedCount: number }> {
      return fetcher.post(
        `${config.apiUrl}/classes/sessions/bulk-update-template`,
        input,
      );
    },
    async previewBulkUpdateInstructors(
      input: BulkUpdateClassSessionInstructorsPreviewDTO,
    ): Promise<BulkUpdateClassSessionInstructorsPreviewResultDTO> {
      return fetcher.post(
        `${config.apiUrl}/classes/sessions/bulk-update-instructors/preview`,
        input,
      );
    },
    async bulkUpdateInstructors(
      input: BulkUpdateClassSessionInstructorsDTO,
    ): Promise<{ updatedCount: number; skippedCount: number }> {
      return fetcher.post(
        `${config.apiUrl}/classes/sessions/bulk-update-instructors`,
        input,
      );
    },
    async create(input: CreateClassSessionDTO): Promise<ClassSessionDTO> {
      return fetcher.post<ClassSessionDTO>(
        `${config.apiUrl}/classes/sessions`,
        input,
      );
    },
    async update(
      id: string,
      input: UpdateClassSessionDTO,
    ): Promise<ClassSessionDTO> {
      return fetcher.put<ClassSessionDTO>(
        `${config.apiUrl}/classes/sessions/${id}`,
        input,
      );
    },
    async getChangeLogs(id: string): Promise<{ logs: ScheduleChangeLogDTO[] }> {
      return fetcher.get<{ logs: ScheduleChangeLogDTO[] }>(
        `${config.apiUrl}/classes/sessions/${id}/change-logs`,
      );
    },
    async complete(id: string): Promise<ClassSessionDTO> {
      return fetcher.post<ClassSessionDTO>(
        `${config.apiUrl}/classes/sessions/${id}/complete`,
        {},
      );
    },
    async correctInstructorCheckIn(
      id: string,
      body: {
        wrongCheckInId: string;
        correctInstructorId: string;
        reason: string;
      },
    ): Promise<{
      removedCheckInId: string;
      removedInstructorId: string;
      removedInstructorName: string;
      newCheckInId: string;
      correctInstructorId: string;
      correctInstructorName: string;
    }> {
      return fetcher.post(
        `${config.apiUrl}/classes/sessions/${id}/correct-instructor-check-in`,
        body,
      );
    },
    async duplicate(id: string): Promise<ClassSessionDTO> {
      return fetcher.post<ClassSessionDTO>(
        `${config.apiUrl}/classes/sessions/${id}/duplicate`,
        {},
      );
    },
    async cancel(
      id: string,
      reason: string,
    ): Promise<{ id: string; status: "cancelled" }> {
      return fetcher.post<{ id: string; status: "cancelled" }>(
        `${config.apiUrl}/classes/sessions/${id}/cancel`,
        { reason },
      );
    },
  },
  scheduleLogs: {
    async list(
      query: ScheduleChangeLogListQueryDTO,
    ): Promise<OffsetPageDTO<ScheduleChangeLogListItemDTO>> {
      const url = new URL(`${config.apiUrl}/classes/schedule-logs`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.sessionType)
        url.searchParams.set("sessionType", query.sessionType);
      if (query.changeType)
        url.searchParams.set("changeType", query.changeType);
      if (query.changedBy) url.searchParams.set("changedBy", query.changedBy);
      if (query.dateFrom) url.searchParams.set("dateFrom", query.dateFrom);
      if (query.dateTo) url.searchParams.set("dateTo", query.dateTo);
      if (query.reason) url.searchParams.set("reason", query.reason);
      return fetcher.get<OffsetPageDTO<ScheduleChangeLogListItemDTO>>(
        url.toString(),
      );
    },
    async get(id: string): Promise<ScheduleChangeLogDetailDTO> {
      return fetcher.get<ScheduleChangeLogDetailDTO>(
        `${config.apiUrl}/classes/schedule-logs/${id}`,
      );
    },
  },
};
